import { classnames } from '@aibees/shared/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Box, Divider, Stack } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { memo } from 'react';
import { useForm } from 'react-hook-form';
import { JsonViewer } from '../json-viewer';
import * as Styled from './styles';
import type { FormProps as Props } from './types';

export const Form = memo(
  ({
    className,
    id,
    scheme,
    loading,
    disbaled,
    defaultValues,
    submitButtonProps,
    customSubmitButton,
    children,
    debug,
    actions = [],
    actionsSx,
    actionDescription,
    actionsPrepend,
    onSubmit,
    resetAfterSubmit,
    noValidate,
    sx,
  }: Props) => {
    const methods = useForm({
      resolver: scheme && yupResolver(scheme),
      defaultValues: defaultValues || undefined,
      mode: 'onChange',
      reValidateMode: 'onChange',
    });
    if (resetAfterSubmit) methods.watch();
    const { handleSubmit, formState } = methods;
    const isDirty = formState.isDirty || !isEmpty(formState.touchedFields);

    actions = [
      ...actions.map((x) => ({
        ...x,
        disabled: loading || formState.isSubmitting,
      })),
      {
        variant: 'contained',
        color: 'primary',
        size: 'small',
        children: 'Save',
        ...submitButtonProps,
        ...({ type: 'submit' } as any),
        loading: loading || formState.isSubmitting,
        disabled: !formState.isValid || submitButtonProps?.disabled,
        // disabled: submitButtonProps?.disabled,
      },
    ];

    const handleSubmitForm: (e?: React.BaseSyntheticEvent) => void = (e) => {
      e?.preventDefault();
      e?.stopPropagation();
      handleSubmit((data, ee) => {
        onSubmit?.(data, ee);
        if (resetAfterSubmit) methods.reset();
      })();
    };

    if (debug) console.log('[form]', ((globalThis as any).formState = methods));

    return (
      <Styled.Wrapper
        className={classnames('Form', className)}
        id={id}
        methods={methods}
        onSubmit={handleSubmitForm}
        noValidate={noValidate}
        sx={sx}
      >
        {debug && (
          <JsonViewer>
            {{
              isValid: formState.isValid,
              isDirty,
              values: methods.getValues(),
              touched: methods.formState.touchedFields,
              errors: methods.formState.errors,
            }}
          </JsonViewer>
        )}

        {children}

        {!customSubmitButton && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Divider />
            <Styled.Actions
              className={classnames({ actionDescription: !!actionDescription })}
              sx={actionsSx}
            >
              {actionsPrepend}
              {actionDescription}
              <Stack direction="row" alignItems="center" gap={1}>
                {actions.map((action, ind) => (
                  <LoadingButton
                    key={ind}
                    {...action}
                    disabled={disbaled || action.disabled}
                  />
                ))}
              </Stack>
            </Styled.Actions>
          </Box>
        )}
      </Styled.Wrapper>
    );
  }
);

Form.displayName = 'Form';
